import React, { useState, useEffect } from 'react';
import { attendanceAPI, employeesAPI } from '../services/api';
import { 
  Calendar, 
  Users, 
  TrendingUp, 
  Clock, 
  FileSpreadsheet,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import NepaliDateConverter from 'nepali-date-converter';
import toast from 'react-hot-toast';
import { getPrimaryEnglishMonthForNepaliMonth, getEnglishDateRangeForNepaliMonth } from '../utils/dateUtils';

const MonthlyReport = () => {
  const [reportData, setReportData] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const getCurrentDate = (system = 'english') => {
    const today = new Date();
    if (system === 'nepali') {
      const nepaliToday = new NepaliDateConverter(today);
      return {
        year: nepaliToday.getYear(),
        month: nepaliToday.getMonth() + 1 // Convert from 0-based to 1-based
      };
    } else {
     
      return {
        year: today.getFullYear(),
        month: today.getMonth() + 1 // Convert from 0-based to 1-based
      };
    }
  };

  const [dateSystem, setDateSystem] = useState('english'); // 'english' or 'nepali'
  const currentDate = getCurrentDate(dateSystem);
  const [selectedYear, setSelectedYear] = useState(currentDate.year);
  const [selectedMonth, setSelectedMonth] = useState(currentDate.month);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [expandedEmployee, setExpandedEmployee] = useState(null);

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const nepaliMonths = [
    { value: 1, label: 'बैशाख', english: 'Baisakh' },
    { value: 2, label: 'जेठ', english: 'Jestha' },
    { value: 3, label: 'आषाढ', english: 'Ashadh' },
    { value: 4, label: 'श्रावण', english: 'Shrawan' },
    { value: 5, label: 'भाद्र', english: 'Bhadra' },
    { value: 6, label: 'आश्विन', english: 'Ashwin' },
    { value: 7, label: 'कार्तिक', english: 'Kartik' },
    { value: 8, label: 'मंसिर', english: 'Mangsir' },
    { value: 9, label: 'पौष', english: 'Poush' },
    { value: 10, label: 'माघ', english: 'Magh' },
    { value: 11, label: 'फाल्गुन', english: 'Falgun' },
    { value: 12, label: 'चैत्र', english: 'Chaitra' }
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  
  // Get current Nepali date for default selection
  const currentNepaliDateObj = new NepaliDateConverter(new Date());
  const currentNepaliDateForYears = currentNepaliDateObj.getBS();
  const nepaliYears = Array.from({ length: 5 }, (_, i) => currentNepaliDateForYears.year - i);

  // Add useEffect to update selected dates when date system changes
  useEffect(() => {
    const newDate = getCurrentDate(dateSystem);
    setSelectedYear(newDate.year);
    setSelectedMonth(newDate.month);
  }, [dateSystem]);

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await employeesAPI.getAll();
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Failed to load employees');
    }
  };

  const fetchMonthlyReport = async () => {
    try {
      setIsLoading(true);
      
      let year = selectedYear;
      let month = selectedMonth;
      
      // If Nepali date system is selected, convert to English date
      if (dateSystem === 'nepali') {
        // Use the new utility function that considers the full month range
        console.log('Debug: Input values - selectedYear:', selectedYear, 'selectedMonth:', selectedMonth);
        const englishDate = getPrimaryEnglishMonthForNepaliMonth(selectedYear, selectedMonth);
        console.log('Debug: English date conversion result:', englishDate);
        year = englishDate.year;
        month = englishDate.month;
        console.log('Debug: Final values - year:', year, 'month:', month);
      }
      
      // For Nepali calendar, we need to calculate the actual date range
      let startDate, endDate;
      if (dateSystem === 'nepali') {
        console.log('Debug: Calculating date range for Nepali year:', selectedYear, 'month:', selectedMonth);
        const dateRange = getEnglishDateRangeForNepaliMonth(selectedYear, selectedMonth);
        console.log('Debug: Date range result:', dateRange);
        if (dateRange) {
          startDate = `${dateRange.start.year}-${dateRange.start.month.toString().padStart(2, '0')}-${dateRange.start.day.toString().padStart(2, '0')}`;
          endDate = `${dateRange.end.year}-${dateRange.end.month.toString().padStart(2, '0')}-${dateRange.end.day.toString().padStart(2, '0')}`;
          console.log('Debug: Final startDate:', startDate, 'endDate:', endDate);
        }
      }

      const response = await attendanceAPI.getMonthlyReport(
        year, 
        month, 
        selectedEmployee || undefined,
        dateSystem,
        startDate,
        endDate
      );
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching monthly report:', error);
      toast.error('Failed to load monthly report');
    } finally {
      setIsLoading(false);
    }
  };

  const exportToExcel = async () => {
    try {
      let year = selectedYear;
      let month = selectedMonth;
      
      // If Nepali date system is selected, convert to English date
      if (dateSystem === 'nepali') {
        // Use the new utility function that considers the full month range
        const englishDate = getPrimaryEnglishMonthForNepaliMonth(selectedYear, selectedMonth);
        year = englishDate.year;
        month = englishDate.month;
      }
      
      // Calculate date range for Nepali calendar if needed
      let startDateExport, endDateExport;
      if (dateSystem === 'nepali') {
        const dateRange = getEnglishDateRangeForNepaliMonth(selectedYear, selectedMonth);
        if (dateRange) {
          startDateExport = `${dateRange.start.year}-${dateRange.start.month.toString().padStart(2, '0')}-${dateRange.start.day.toString().padStart(2, '0')}`;
          endDateExport = `${dateRange.end.year}-${dateRange.end.month.toString().padStart(2, '0')}-${dateRange.end.day.toString().padStart(2, '0')}`;
        }
      }

      const response = await attendanceAPI.exportMonthlyToExcel(
        year,
        month,
        selectedEmployee || undefined,
        dateSystem,
        startDateExport,
        endDateExport
      );
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get month name based on selected date system
      const monthName = dateSystem === 'nepali' 
        ? nepaliMonths.find(m => m.value === selectedMonth)?.english
        : months.find(m => m.value === selectedMonth)?.label;
      
      const employeeName = selectedEmployee ? 
        employees.find(e => e._id === selectedEmployee)?.name : 'All';
      
      const dateSystemLabel = dateSystem === 'nepali' ? 'Nepali' : 'English';
      link.download = `Monthly_Report_${monthName}_${selectedYear}_${employeeName}_${dateSystemLabel}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Report exported successfully');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    }
  };

  const toggleEmployeeDetails = (employeeId) => {
    setExpandedEmployee(expandedEmployee === employeeId ? null : employeeId);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Monthly Attendance Report</h2>
          <p className="text-gray-600">Detailed monthly attendance analysis</p>
        </div>
        <button
          onClick={exportToExcel}
          disabled={!reportData}
          className="btn btn-primary flex items-center gap-2 disabled:opacity-50"
        >
          <FileSpreadsheet className="w-4 h-4" />
          Export Excel
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        {/* Date System Toggle */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Date System</label>
          <div className="flex space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                value="english"
                checked={dateSystem === 'english'}
                onChange={(e) => setDateSystem(e.target.value)}
                className="mr-2"
              />
              English Calendar
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="nepali"
                checked={dateSystem === 'nepali'}
                onChange={(e) => setDateSystem(e.target.value)}
                className="mr-2"
              />
              नेपाली पात्रो
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {dateSystem === 'nepali' ? 'वर्ष' : 'Year'}
            </label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              {(dateSystem === 'nepali' ? nepaliYears : years).map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {dateSystem === 'nepali' ? 'महिना' : 'Month'}
            </label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              {(dateSystem === 'nepali' ? nepaliMonths : months).map(month => (
                <option key={month.value} value={month.value}>
                  {dateSystem === 'nepali' ? month.label : month.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
            <select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">All Employees</option>
              {employees.map(employee => (
                <option key={employee._id} value={employee._id}>
                  {employee.name} ({employee.employeeId})
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={fetchMonthlyReport}
              className="btn btn-primary w-full"
              disabled={isLoading}
            >
              {isLoading ? 'Loading...' : 'Generate Report'}
            </button>
          </div>
        </div>
      </div>

      {/* Report Data */}
      {reportData && (
        <>
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Working Days</p>
                  <p className="text-lg font-semibold text-gray-900">{reportData.summary.totalWorkingDays}</p>
                </div>
                <Calendar className="w-5 h-5 text-gray-500" />
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Average Attendance</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {reportData.summary.overallAttendancePercentage.toFixed(1)}%
                  </p>
                </div>
                <TrendingUp className="w-8 h-8 text-green-500" />
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Employees</p>
                  <p className="text-lg font-semibold text-gray-700">{reportData.employees.length}</p>
                </div>
                <Users className="w-5 h-5 text-gray-500" />
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Avg Working Hours</p>
                  <p className="text-lg font-semibold text-gray-700">
                    {reportData.summary.averageWorkingHours.toFixed(1)}h
                  </p>
                </div>
                <Clock className="w-5 h-5 text-gray-500" />
              </div>
            </div>
          </div>

          {/* Employee Details */}
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Employee Attendance Details</h3>
            </div>
            <div className="divide-y divide-gray-200">
              {reportData.employees.map((employee) => (
                <div key={employee.employee._id} className="p-6">
                  <div 
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => toggleEmployeeDetails(employee.employee._id)}
                  >
                    <div className="flex items-center space-x-4">
                      <div>
                        <h4 className="text-lg font-medium text-gray-900">{employee.employee.name}</h4>
                        <p className="text-sm text-gray-600">
                          {employee.employee.employeeId} • {employee.employee.department}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-6">
                      <div className="text-center">
                        <p className="text-sm text-gray-600">Present</p>
                        <p className="text-lg font-semibold text-gray-900">{employee.presentDays}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-gray-600">Absent</p>
                        <p className="text-lg font-semibold text-red-600">{employee.absentDays}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-gray-600">Late</p>
                        <p className="text-lg font-semibold text-gray-700">{employee.lateDays}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-gray-600">Attendance</p>
                        <p className="text-lg font-semibold text-gray-700">
                          {employee.attendancePercentage.toFixed(1)}%
                        </p>
                      </div>
                      {expandedEmployee === employee.employee._id ? 
                        <ChevronUp className="w-5 h-5 text-gray-400" /> : 
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      }
                    </div>
                  </div>
                  
                  {expandedEmployee === employee.employee._id && employee.attendanceDetails && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <p className="text-sm font-medium text-gray-700">Total Working Hours</p>
                          <p className="text-lg font-semibold text-gray-900">
                            {employee.workingHours.toFixed(1)}h
                          </p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <p className="text-sm font-medium text-gray-700">Average Daily Hours</p>
                          <p className="text-lg font-semibold text-gray-900">
                            {(employee.workingHours / employee.presentDays || 0).toFixed(1)}h
                          </p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <p className="text-sm font-medium text-gray-700">Holiday Work Days</p>
                          <p className="text-lg font-semibold text-gray-700">
                            {employee.holidayWorkDays || 0}
                          </p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <p className="text-sm font-medium text-gray-700">Holiday Work Hours</p>
                          <p className="text-lg font-semibold text-gray-700">
                            {(employee.holidayWorkHours || 0).toFixed(1)}h
                          </p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <p className="text-sm font-medium text-gray-700">Half Days</p>
                          <p className="text-lg font-semibold text-gray-900">0</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {isLoading && (
        <div className="bg-white p-8 rounded-lg shadow-sm border text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Generating monthly report...</p>
        </div>
      )}

      {!reportData && !isLoading && (
        <div className="bg-white p-8 rounded-lg shadow-sm border text-center">
          <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Select filters and click "Generate Report" to view monthly attendance data</p>
        </div>
      )}
    </div>
  );
};

export default MonthlyReport;
